import {
    Card,
    CardMedia,
    CardContent,
    Typography,
    ImageList,
    ImageListItem,
    useTheme,
    useMediaQuery,
    Button,
    Grid,
    Container
} from "@mui/material";
import { Link } from 'react-router-dom';
import {Carousel} from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import Box from "@mui/material/Box";
import {styled} from '@mui/material/styles';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useEffect, useState } from "react";
import { fetchNews } from "../api/newsService";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';

const carouselImgs = [
    "/images/diaporama/Octobre_Rose_Groupe.jpg",
    "/images/diaporama/AGRentree2025.jpg",
    "/images/diaporama/Rentree2025.jpg",
    "/images/diaporama/Apero3B.jpg",
    "/images/diaporama/CoupeFemme.jpg",
];

const emojiMap = {
    "coeur": "🩷",
    "catamaran": "⛵",
    "rame": "🚣",
    "pluie": "💧",
    "plage": "🏖️",
    "mer": "🌊",
    "lac": "🏞️",
    "sport": "🏃",
    "vélo": "🚴",
    "marche": "🚶",
    "course": "🏃‍♂️",
    "montagne": "⛰️",
    "nature": "🌿",
    "balade": "🥾",
    "fêter": "🎉",
    "rire": "😂",
    "famille": "👨‍👩‍👧‍👦",
    "enfants": "🧒",
    "ami": "👫",
    "groupe": "👥",
    "main": "🤚",
    "aide": "🆘",
    "soleil": "☀️",
    "nuit": "🌙",
    "matin": "🌅",
    "soir": "🌇",
    "hiver": "❄️",
    "été": "🌞",
    "burger": "🍔",
    "pizza": "🍕",
    "boisson": "🥤",
    "repas": "🍽️",
    "fête": "🥳",
    "musique": "🎶",
    "photo": "📸",
    "vidéo": "🎥",
    "cliquez": "👉",
    "info": "ℹ️",
    "contact": "📞",
    "email": "✉️",
    "web": "🌐",
};

function insertEmojis(text) {
    return text.replace(/\b(\w+)\b/gi, (match) => {
        const lower = match.toLowerCase();
        return emojiMap[lower] ? `${emojiMap[lower]} ${match}` : match;
    });
}

const ExpandMore = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'expand',
})(({ theme, expand }) => ({
    marginLeft: 'auto',
    transform: expand ? 'rotate(0deg)' : 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}));

export default function Actualites() {
    const [posts, setPosts] = useState([]);
    const [loadingNews, setLoadingNews] = useState(true);
    const [newsError, setNewsError] = useState(null);
    const [expandedPosts, setExpandedPosts] = useState({});
    const [openImage, setOpenImage] = useState(null); // { url, title } ou null

    const handleOpenImage = (url, title) => {
        setOpenImage({ url, title });
    };
    const handleCloseImage = () => {
        setOpenImage(null);
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoadingNews(true);
            try {
                const loaded = await fetchNews();
                if (mounted) setPosts(loaded);
            } catch (err) {
                if (mounted) setNewsError(err.message || "Erreur de chargement");
            } finally {
                if (mounted) setLoadingNews(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check if screen is mobile (width < 600px)

    const handleExpandClick = (postId) => {
        setExpandedPosts((prev) => ({
            ...prev,
            [postId]: !prev[postId],
        }));
    };

    if (loadingNews) return <Typography>Chargement des actualités…</Typography>;
    if (newsError) return <Typography color="error">Erreur: {newsError}</Typography>;

    return (
        <Box sx={{ paddingBottom: '50px', paddingTop: '5px'}}>
            <Carousel showThumbs={false} autoPlay infiniteLoop dynamicHeight animationHandler={'fade'} swipeable={false}>
                {carouselImgs.map((img, index) => (
                    <Box key={index}
                         sx={{position: "relative", height:"60%", paddingTop: "56.25%", paddingBottom: '50px'}}>
                        <img src={img} alt="Vitréenne Volley-ball"
                             style={{
                                 position: "absolute",
                                 top: 0,
                                 left: 0,
                                 width: "100%",
                                 height: "60%",
                                 objectFit: "cover",
                             }}/>
                    </Box>
                ))}
            </Carousel>

            <Box sx={{display: "flex", flexDirection: "column", gap: 3, paddingTop: '10px'}}>
                <Container>
                    {/*<Typography variant="h4" align="center" gutterBottom sx={{fontWeight: 700}}>*/}
                    {/*    Le Club*/}
                    {/*</Typography>*/}

                    {/* Bloc de présentation centré, largeur max, fond léger, accent visuel */}
                    <Box sx={{
                        backgroundColor: '#fbfdff', // léger fond bleu très pâle
                        p: { xs: 2, md: 4 },
                        borderRadius: 3,
                        boxShadow: 2,
                        maxWidth: '100%',
                        margin: '3em auto 28px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        textAlign: { xs: 'left', md: 'justify' }
                    }}>
                        {/* Accent coloré à gauche + petit titre */}
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                            <Box sx={{width: 8, height: 40, backgroundColor: '#df0707cf', borderRadius: 1}} />
                            <Typography variant="h6" sx={{fontWeight: 700, letterSpacing: 0.2}}>Le Club</Typography>
                        </Box>

                        <Typography variant="body1" sx={{whiteSpace: 'pre-line', lineHeight: 1.5, color: 'text.primary', fontSize: { xs: '0.95rem', md: '1.15rem' }, width: '100%' }}>
                            La section loisirs accueille toutes les personnes souhaitant jouer au volley de 17 à 99 ans.
                            <br/>Il y a possibilité de faire uniquement les entraînements ou alors de faire partie d'une équipe mixte avec des matchs dans le championnat Compet'lib.
                            <br/><br/>Les entraînements ont lieu au complexe sportif des promenades à Vitré tous les <b>mardis (salle A) et jeudis (salle B) de 20h à 22h</b>.
                            <br/>Dès la saison 2026/2027, nous ouvrons une section jeunes pour les enfants de 9 à 11 ans.
                            <br/><br/>N'hésitez pas à nous contacter.
                            <br/><br/><br/><br/>
                        </Typography>
                        <Button
                            component={Link}
                            to="/nous-contacter"
                            style={{
                                borderRadius: 35,
                                backgroundColor: "#b71c1ccf",
                                padding: "18px 36px",
                                fontSize: "18px",
                                width: '40%',
                                marginLeft:"auto",
                                marginRight:"auto"
                            }}
                            variant="contained"
                        >
                            Nous Contacter
                        </Button>
                    </Box>
                </Container>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 4,
                        alignItems: 'center',
                        flexWrap: 'nowrap', // force une seule ligne
                    }}
                >
                    <img
                        src="/images/sponsors/guy_xvi.png"
                        alt="Sponsor 1"
                        loading="lazy"
                        style={{
                            maxHeight: isMobile ? 60 : 120,
                            width: 'auto',
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                    <img
                        src="/images/sponsors/loge_de_mathilde.png"
                        alt="Sponsor 2"
                        loading="lazy"
                        style={{
                            maxHeight: isMobile ? 60 : 120,
                            width: 'auto',
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                </Box>
            </Box>
            <Box sx={{
                flexDirection: "column",
                gap: 3,
                paddingTop: '100px',
                maxWidth: '1100px',
                margin:"auto",
                backgroundColor:"#ffff"
            }}>
                <Grid container spacing={3} rowGap={4} justifyContent="center">
                    {posts.map((post, index) => (
                        <Grid item key={index} xs={12} sm={6} md={6} sx={{ display: 'flex' }}>
                            <Card
                                sx={{
                                    backgroundColor:"#fbfdff",
                                    width: '100%',
                                    boxShadow: 3,
                                }}
                            >
                                <CardHeader
                                    title={post.title}
                                    subheader={post.date}
                                    sx={{
                                        backgroundColor:"#fbfdff"
                                    }}
                                />
                                {(post.images) &&
                                    <CardMedia
                                        component="img"
                                        image={post?.images[0]?.url}
                                        alt={post?.images[0]?.title}
                                        sx={{
                                            width: "90%",
                                            height: 300, // taille fixe pour que toutes les CardMedia aient la même hauteur
                                            display: "block",
                                            margin: "0 auto",
                                            cursor: "pointer",
                                            objectFit: 'cover' // recadrage pour garder un rendu uniforme
                                        }}
                                        onClick={() => post?.images?.[0] && handleOpenImage(post.images[0].url, post.images[0].title)}
                                    />
                                }
                                <CardContent sx={{ backgroundColor:"#fbfdff"}}>
                                    <Typography variant="h6" sx={{ color: 'text.secondary'}}>
                                        {(() => {
                                            // Normalise les séquences littérales "\\r\\n" et "\\n" en vrais sauts de ligne
                                            const raw = post?.text || '';
                                            const normalized = raw
                                                .replace(/\\r\\n/g, '\n') // texte contenant les deux caractères backslash+r backslash+n
                                                .replace(/\\n/g, '\n')    // texte contenant les deux caractères backslash+n
                                                .replace(/\r\n/g, '\n');  // véritables retours chariot

                                            return normalized.split('\n').map((line, idxLine) => {
                                                const withEmojis = insertEmojis(line);
                                                const parts = withEmojis.split('<mylink>');
                                                return (
                                                    <Box key={idxLine} component="p" sx={{ mb: 1 }}>
                                                        {parts.map((part, idx) =>
                                                            idx === parts.length - 1 ? (
                                                                part
                                                            ) : (
                                                                <React.Fragment key={`link-${idxLine}-${idx}`}>
                                                                    {part}
                                                                    {post.mylink && (
                                                                        <a
                                                                            href={post.mylink.value}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            style={{ color: '#1976d2', textDecoration: 'underline', margin: '0 .5rem' }}
                                                                        >
                                                                            {post.mylink.text}
                                                                        </a>
                                                                    )}
                                                                </React.Fragment>
                                                            )
                                                        )}
                                                    </Box>
                                                );
                                            });
                                        })()}
                                    </Typography>
                                </CardContent>
                                <CardActions disableSpacing>
                                    <ExpandMore
                                        expand={expandedPosts[post.id] || false}
                                        onClick={() => handleExpandClick(post.id)}
                                        aria-expanded={expandedPosts[post.id] || false}
                                        aria-label="plus de détail"
                                    >
                                        <Typography variant="h5">Plus de photos</Typography>
                                        <ExpandMoreIcon/>
                                    </ExpandMore>
                                </CardActions>
                                <Collapse in={expandedPosts[post.id]} timeout="auto" unmountOnExit>
                                    <CardContent>
                                        <ImageList cols={isMobile ? 1 : 2} gap={8}>
                                            {post?.images.map((item) => (
                                                <ImageListItem key={item.url} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <img
                                                        srcSet={`${item.url}?w=492&h=492&fit=scale&auto=format&dpr=2 2x`}
                                                        src={`${item.url}?w=492&h=492&fit=scale&auto=format`}
                                                        alt={item.title || 'image'}
                                                        loading="lazy"
                                                        style={{
                                                            width: "100%",
                                                            height: 200,
                                                            objectFit: "cover",
                                                            cursor: "pointer",
                                                            borderRadius: 4
                                                        }}
                                                        onClick={() => handleOpenImage(item.url, item.title)}
                                                    />
                                                </ImageListItem>
                                            ))}
                                        </ImageList>
                                    </CardContent>
                                </Collapse>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
            <Dialog
                open={!!openImage}
                onClose={handleCloseImage}
                fullWidth
                maxWidth="xl"
                fullScreen={isMobile} // utiliser plein écran sur mobile
                PaperProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }
                }}
            >
                <IconButton
                    onClick={handleCloseImage}
                    sx={{ position: 'absolute', top: 8, right: 8, color: 'white', zIndex: 1301 }}
                    aria-label="fermer"
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 2,
                        backgroundColor: 'rgba(0,0,0,0.85)',
                        borderRadius: 1
                    }}
                >
                    {openImage && (
                        <img
                            src={openImage.url}
                            alt={openImage.title || 'image'}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '90vh',
                                objectFit: 'contain',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}